/**
 * schemaOrg.js — Blocs JSON-LD centralisés pour EmpCo Compliance (GEO Lot 2).
 * Version 1.0 — 30 septembre 2026.
 *
 * Séparation des entités : « EmpCo Compliance » (logiciel) ≠ « directive EmpCo » (texte de loi).
 * Les @id sont stables et globaux pour permettre aux moteurs génératifs (ChatGPT, Perplexity,
 * Le Chat de Mistral, Claude, Gemini) de relier chaque page à la même organisation, au même site
 * et au même texte législatif officiel.
 *
 * Placeholders visibles côté code : SIREN, adresse siège, LinkedIn auteur.
 * À renseigner par l'éditeur — ne pas inventer.
 */

export const ORIGIN = "https://empco-compliance.eu";
export const ORG_ID = `${ORIGIN}/#organization`;
export const WEBSITE_ID = `${ORIGIN}/#website`;
export const SOFTWARE_ID = `${ORIGIN}/#software`;
export const DIRECTIVE_ELI = "http://data.europa.eu/eli/dir/2024/825/oj";

/** Date de dernière modification globale des pages piliers/guides — Lot 2 GEO. */
export const CONTENT_LAST_MODIFIED = "2026-09-30";
export const DIRECTIVE_APPLICABLE_SINCE = "2026-09-27";

/** Auteur canonique des contenus réglementaires (E-E-A-T). */
export const AUTHOR = {
  "@type": "Person",
  name: "Nicolas Philizor",
  jobTitle: "Fondateur, responsable éditorial réglementaire",
  url: `${ORIGIN}/a-propos#author`,
  sameAs: [
    "https://www.linkedin.com/in/nicolas-philizor-0344a7206/",
  ],
};

/** Bloc 1 — Organization + WebSite (à injecter sur toutes les pages). */
export function orgAndWebSite(lang = "fr") {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: "EmpCo Compliance",
        legalName: "EmpCo SAS",
        alternateName: ["EmpCo SAS", "empco-compliance.eu"],
        disambiguatingDescription:
          "Éditeur français du logiciel EmpCo Compliance. Ne pas confondre avec la directive (UE) 2024/825, également appelée « directive EmpCo ».",
        url: `${ORIGIN}/`,
        logo: { "@type": "ImageObject", url: `${ORIGIN}/brand/logo-full.png` },
        email: "contact@empco-compliance.eu",
        foundingDate: "2025",
        // Adresse et SIREN à compléter par l'éditeur
        address: {
          "@type": "PostalAddress",
          streetAddress: "[ADRESSE DU SIÈGE À COMPLÉTER]",
          postalCode: "[CP]",
          addressLocality: "[VILLE]",
          addressCountry: "FR",
        },
        identifier: [
          { "@type": "PropertyValue", "propertyID": "SIREN", value: "[SIREN À COMPLÉTER]" },
        ],
        areaServed: ["FR", "DE", "ES", "IT", "NL", "BE", "LU", "AT"],
        knowsAbout: [
          "Directive (UE) 2024/825",
          "Allégations environnementales",
          "Greenwashing",
          "Pratiques commerciales trompeuses",
          "Labels de durabilité",
          "DGCCRF",
        ],
        sameAs: [
          "https://www.linkedin.com/company/146606902/",
          "https://www.youtube.com/@EMPCOCOMPLIANCE",
        ],
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: "EmpCo Compliance",
        url: `${ORIGIN}/`,
        inLanguage: ["fr", "en", "de", "es", "it", "nl"],
        publisher: { "@id": ORG_ID },
      },
    ],
  };
}

/** Bloc 2 — SoftwareApplication avec UnitPriceSpecification HT (accueil, /produit, /tarifs). */
export function softwareApplication(lang = "fr", url) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": SOFTWARE_ID,
    name: "EmpCo Compliance",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Conformité réglementaire, allégations environnementales",
    operatingSystem: "Web",
    url: url || `${ORIGIN}/`,
    inLanguage: lang,
    description:
      "Logiciel SaaS qui détecte, qualifie et documente les allégations environnementales d'un site web, d'un packaging ou d'un document au regard de la directive (UE) 2024/825, avec registre versionné, coffre de preuves et dossier de contrôle horodaté. Ne délivre ni conseil juridique, ni label, ni certificat de conformité.",
    featureList: [
      "Scan de site web jusqu'à 200 pages",
      "Analyse d'image de packaging ou d'affiche",
      "Analyse de document PDF, DOCX ou TXT",
      "Qualification des allégations par intelligence artificielle",
      "Propositions de reformulation",
      "Registre des allégations versionné et daté",
      "Coffre de preuves avec alertes d'expiration à 90, 30 et 7 jours",
      "Recrawl hebdomadaire et détection des nouvelles allégations",
      "Dossier de contrôle PDF horodaté en un clic",
    ],
    publisher: { "@id": ORG_ID },
    offers: [
      {
        "@type": "Offer",
        name: "Scan gratuit",
        price: "0",
        priceCurrency: "EUR",
        description: "Jusqu'à 15 pages, décompte des signaux par gravité, sans verdict de conformité.",
      },
      {
        "@type": "Offer",
        name: "Scan complet",
        priceCurrency: "EUR",
        url: `${ORIGIN}/tarifs`,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "20",
          priceCurrency: "EUR",
          valueAddedTaxIncluded: false,
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "analyse" },
        },
      },
      {
        "@type": "Offer",
        name: "Surveillance continue",
        priceCurrency: "EUR",
        url: `${ORIGIN}/tarifs`,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "200",
          priceCurrency: "EUR",
          valueAddedTaxIncluded: false,
          billingDuration: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
          unitText: "mois",
        },
      },
    ],
  };
}

/** Bloc 3 — WebPage + entité Legislation (page /legal et /legal/directive-2024-825). */
export function webPageWithLegislation({ url, name, lang = "fr", datePublished = "2026-05-04", dateModified = CONTENT_LAST_MODIFIED }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name,
    inLanguage: lang,
    datePublished,
    dateModified,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    about: legislationEntity(lang),
  };
}

/** Entité Legislation réutilisable — clé pour le GEO (peu utilisée par la concurrence). */
export function legislationEntity(lang = "fr") {
  return {
    "@type": "Legislation",
    "@id": DIRECTIVE_ELI,
    name:
      "Directive (UE) 2024/825 du Parlement européen et du Conseil du 28 février 2024 modifiant les directives 2005/29/CE et 2011/83/UE pour donner aux consommateurs les moyens d'agir en faveur de la transition verte grâce à une meilleure protection contre les pratiques déloyales et grâce à une meilleure information",
    alternateName: ["Directive EmpCo", "Empowering Consumers for the Green Transition", "ECGT"],
    legislationIdentifier: "32024L0825",
    legislationType: "Directive",
    legislationDate: "2024-02-28",
    legislationJurisdiction: "Union européenne",
    legislationLegalForce: "InForce",
    url: "https://eur-lex.europa.eu/eli/dir/2024/825/oj",
    sameAs: `https://eur-lex.europa.eu/legal-content/${lang.toUpperCase()}/TXT/?uri=CELEX:32024L0825`,
  };
}

/** Bloc 4 — Article daté et signé (pages piliers /conformite/*, /secteurs/*, /guides/*). */
export function articleBlock({
  url,
  headline,
  description,
  lang = "fr",
  datePublished = "2026-05-04",
  dateModified = CONTENT_LAST_MODIFIED,
  image = `${ORIGIN}/brand/og-cover.png`,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline,
    description,
    inLanguage: lang,
    datePublished,
    dateModified,
    mainEntityOfPage: url,
    image,
    author: AUTHOR,
    publisher: { "@id": ORG_ID },
    about: { "@id": DIRECTIVE_ELI },
    citation: [
      "https://eur-lex.europa.eu/eli/dir/2024/825/oj",
      "https://eur-lex.europa.eu/eli/dir/2005/29/oj",
      "https://www.legifrance.gouv.fr/codes/id/LEGITEXT000006069565/",
    ],
  };
}

/** Bloc 5 — DefinedTermSet (glossaire /conformite/termes-interdits). */
export function definedTermSet(lang = "fr", url) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": `${url}#glossaire`,
    name: "Allégations environnementales génériques visées par la directive (UE) 2024/825",
    inLanguage: lang,
    hasDefinedTerm: [
      {
        "@type": "DefinedTerm",
        name: "écologique",
        description:
          "Allégation environnementale générique. Utilisable uniquement si une excellence environnementale reconnue est démontrée et explicitée sur le même support. Reformulation type : indiquer le fait mesuré, son périmètre et sa méthode.",
      },
      {
        "@type": "DefinedTerm",
        name: "neutre en carbone",
        description:
          "Allégation fondée sur la compensation des émissions. Interdite lorsqu'elle présente un produit comme ayant un impact neutre, réduit ou positif sur la base de la seule compensation. Reformulation type : empreinte mesurée par unité, méthode et périmètre.",
      },
      {
        "@type": "DefinedTerm",
        name: "respectueux de l'environnement",
        description: "Allégation environnementale générique. Même régime que « écologique ».",
      },
      {
        "@type": "DefinedTerm",
        name: "éco-friendly",
        description:
          "Allégation environnementale générique en anglais, soumise au même régime que « écologique » lorsqu'elle s'adresse à des consommateurs de l'Union.",
      },
      {
        "@type": "DefinedTerm",
        name: "vert",
        description:
          "Adjectif générique. Ne peut qualifier un produit sans démonstration d'un bénéfice environnemental mesuré et vérifiable.",
      },
    ],
  };
}

/**
 * Bloc 6 — BreadcrumbList (toutes les pages sauf accueil).
 * @param {Array<{name:string,url:string}>} items - Items du fil d'ariane (incl. accueil en position 1)
 */
export function breadcrumbList(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

/**
 * Construit un fil d'ariane simple à partir d'un chemin URL et d'un titre H1.
 * Ex : /conformite/termes-interdits → Accueil > Conformité > Termes interdits
 */
export function breadcrumbFromPath(path, currentName, lang = "fr") {
  const homeUrl = lang === "fr" ? `${ORIGIN}/` : `${ORIGIN}/${lang}`;
  const items = [{ name: lang === "fr" ? "Accueil" : "Home", url: homeUrl }];
  const parts = path.split("/").filter(Boolean);
  // Dernier segment = page courante avec son H1
  if (parts.length >= 2) {
    // Segment intermédiaire (ex: /conformite)
    const midName = _humanize(parts[parts.length - 2]);
    items.push({ name: midName, url: `${ORIGIN}/${parts.slice(0, parts.length - 1).join("/")}` });
  }
  items.push({ name: currentName, url: `${ORIGIN}${path}` });
  return breadcrumbList(items);
}

function _humanize(slug) {
  const map = {
    conformite: "Conformité",
    cumplimiento: "Cumplimiento",
    naleving: "Naleving",
    conformita: "Conformità",
    compliance: "Compliance",
    secteurs: "Secteurs",
    sectors: "Sectors",
    sectores: "Sectores",
    settori: "Settori",
    sectoren: "Sectoren",
    branchen: "Branchen",
    guides: "Guides",
    guias: "Guías",
    guide: "Guide",
    gidsen: "Gidsen",
    ratgeber: "Ratgeber",
    legal: "Legal",
  };
  return map[slug] || slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");
}

/**
 * Convertit un tableau de sources textuelles en items {label, href} cliquables.
 * Reconnaît les textes standards du corpus FR/EN/... et renvoie l'URL officielle.
 */
export function linkifySources(sources) {
  return (sources || []).map((s) => {
    if (typeof s !== "string") return { label: String(s), href: null };
    const lower = s.toLowerCase();
    if (lower.includes("2024/825") || lower.includes("empco")) {
      return { label: s, href: "https://eur-lex.europa.eu/eli/dir/2024/825/oj" };
    }
    if (lower.includes("2005/29")) {
      return { label: s, href: "https://eur-lex.europa.eu/eli/dir/2005/29/oj" };
    }
    if (lower.includes("l.121") || lower.includes("code de la consommation") || lower.includes("legifrance") || lower.includes("verbraucherschutz") || lower.includes("consumo")) {
      return { label: s, href: "https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069565/LEGISCTA000032227103/" };
    }
    if (lower.includes("dgccrf")) {
      return { label: s, href: "https://www.economie.gouv.fr/dgccrf" };
    }
    if (lower.includes("cpc") || lower.includes("2017/2394")) {
      return { label: s, href: "https://eur-lex.europa.eu/eli/reg/2017/2394/oj" };
    }
    return { label: s, href: null };
  });
}

/** Format « Mis à jour le 30 septembre 2026 » pour un affichage FR (utilisé sous H1). */
export function formatUpdatedFR(dateIso = CONTENT_LAST_MODIFIED) {
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  const d = new Date(dateIso + "T00:00:00Z");
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
